// Export Workflow — the inline landing page (UX redesign slice A). Shown in the empty Export
// Assistant card before a run starts: the transaction description, then "How much should we
// check?" (full review vs specific checks, with a checkbox tile per check). Start hands the
// choice back to EARChatUI, which puts it in workflow_state._control and sends the opening
// message. Tiles are pre-selected from the description ("Suggested") until the user touches the
// choice (decision A-D1); the backend detector is authoritative, the keyword guess only bridges
// the wait.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import GppMaybeOutlinedIcon from '@mui/icons-material/GppMaybeOutlined';
import {
  DEFAULT_EXPORT_SCOPE_CHOICE,
  EXPORT_SCOPE_CHIP_LABEL,
  EXPORT_SCOPE_PHASE_ORDER,
  ExportScopeChoice,
  ExportScopeMode,
  ExportScopePhase,
  choiceFromSuggestion,
  fetchScopeSuggestion,
  keywordScopeGuess,
  scopeAutoIncluded,
} from 'utils/exportScope';

/** The server detector runs once the description is this long, 700 ms after the last keystroke. */
const SUGGEST_MIN_CHARS = 12;
const SUGGEST_DEBOUNCE_MS = 700;

const DESCRIPTION_PLACEHOLDER =
  'Describe what you are exporting, where it is going and what the end-use will be...';

interface ExportLandingProps {
  value: string;
  onChange: (value: string) => void;
  onStart: (choice: ExportScopeChoice) => void;
  /** The PHI / data-boundary notice shown under the textarea (EXPORT_DATA_BOUNDARY_NOTICE). */
  dataNotice: string;
  disabled?: boolean;
  inputRef?: React.Ref<HTMLTextAreaElement>;
}

const joinLabels = (labels: string[]) =>
  labels.length < 2
    ? labels.join('')
    : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;

/** "Also runs Schedule B / HTS, because EAR / CCL uses it." — one line per auto-added check. */
export const prerequisiteLines = (selected: ExportScopePhase[]): string[] => {
  const autoAdded = scopeAutoIncluded(selected);
  return EXPORT_SCOPE_PHASE_ORDER.filter((p) => autoAdded[p]?.length).map((prereq) => {
    const needers = (autoAdded[prereq] || []).map((p) => EXPORT_SCOPE_CHIP_LABEL[p]);
    return `Also runs ${EXPORT_SCOPE_CHIP_LABEL[prereq]}, because ${joinLabels(needers)} ${
      needers.length > 1 ? 'use' : 'uses'
    } it.`;
  });
};

export const startButtonLabel = (choice: ExportScopeChoice): string => {
  if (choice.scope === 'full') return 'Start full review';
  const n = choice.selectedPhases.length;
  if (!n) return 'Run checks';
  return `Run ${n} ${n === 1 ? 'check' : 'checks'}`;
};

const ExportLanding: React.FC<ExportLandingProps> = ({
  value,
  onChange,
  onStart,
  dataNotice,
  disabled = false,
  inputRef,
}) => {
  const [choice, setChoice] = useState<ExportScopeChoice>(DEFAULT_EXPORT_SCOPE_CHOICE);
  const [suggested, setSuggested] = useState<ExportScopePhase[]>([]);
  // Once the user clicks a radio or a tile, suggestions never change the choice again.
  const [touched, setTouched] = useState(false);
  const touchedRef = useRef(false);

  const applySuggestion = (next: ExportScopeChoice) => {
    setChoice(next);
    setSuggested(next.scope === 'specific' ? next.selectedPhases : []);
  };

  useEffect(() => {
    if (touched || disabled) return undefined;
    const text = value.trim();
    // Instant keyword pre-highlight; a miss keeps the last suggestion until the server answers,
    // so the tiles don't flicker on every keystroke.
    const guess = keywordScopeGuess(text);
    if (guess) {
      applySuggestion(guess);
    } else if (text.length < SUGGEST_MIN_CHARS) {
      applySuggestion(DEFAULT_EXPORT_SCOPE_CHOICE);
    }
    if (text.length < SUGGEST_MIN_CHARS) return undefined;

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      const suggestion = await fetchScopeSuggestion(text, controller.signal);
      // A failed call ('default' / null) keeps the keyword guess; the detector's answer wins.
      if (controller.signal.aborted || touchedRef.current) return;
      if (!suggestion || suggestion.source !== 'detector') return;
      applySuggestion(choiceFromSuggestion(suggestion));
    }, SUGGEST_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [value, touched, disabled]);

  const markTouched = () => {
    touchedRef.current = true;
    setTouched(true);
  };

  const selectMode = (scope: ExportScopeMode) => {
    markTouched();
    setChoice((prev) => ({ ...prev, scope: scope }));
  };

  const togglePhase = (phase: ExportScopePhase) => {
    markTouched();
    setChoice((prev) => {
      const picked = prev.selectedPhases.includes(phase)
        ? prev.selectedPhases.filter((p) => p !== phase)
        : [...prev.selectedPhases, phase];
      return {
        ...prev,
        selectedPhases: EXPORT_SCOPE_PHASE_ORDER.filter((p) => picked.includes(p)),
      };
    });
  };

  const isSpecific = choice.scope === 'specific';
  const prereqLines = useMemo(
    () => (isSpecific ? prerequisiteLines(choice.selectedPhases) : []),
    [isSpecific, choice.selectedPhases],
  );
  const canStart =
    !disabled && Boolean(value.trim()) && (!isSpecific || choice.selectedPhases.length > 0);

  const start = () => {
    if (!canStart) return;
    onStart(choice);
  };

  return (
    <div className='export-landing'>
      <form
        className='export-landing-inner'
        onSubmit={(event) => {
          event.preventDefault();
          start();
        }}
      >
        <div className='export-landing-intro'>
          <h2 className='export-landing-title'>What are you exporting?</h2>
          <p className='export-landing-subtitle'>
            Describe the product, where it&apos;s going and who will use it. Then choose how
            much we check.
          </p>
        </div>

        <div className='export-landing-field'>
          <label className='export-landing-label' htmlFor='export-landing-description'>
            Transaction details
          </label>
          <textarea
            id='export-landing-description'
            ref={inputRef}
            className='export-landing-textarea'
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={(event) => {
              // Enter adds a line (this is a form field); Ctrl/Cmd+Enter starts the review.
              if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                start();
              }
            }}
            placeholder={DESCRIPTION_PLACEHOLDER}
            disabled={disabled}
            rows={4}
          />
          <div className='export-landing-notice'>
            <GppMaybeOutlinedIcon className='export-landing-notice-icon' aria-hidden='true' />
            <span>{dataNotice}</span>
          </div>
        </div>

        <fieldset className='export-landing-fieldset' disabled={disabled}>
          <legend className='export-landing-label'>How much should we check?</legend>
          <div className='export-landing-modes'>
            <label className={`export-landing-mode${!isSpecific ? ' is-selected' : ''}`}>
              <input
                type='radio'
                name='export-landing-scope'
                className='export-landing-visually-hidden'
                checked={!isSpecific}
                onChange={() => selectMode('full')}
                onClick={markTouched}
              />
              <span className='export-landing-radio' aria-hidden='true' />
              <span className='export-landing-mode-body'>
                <span className='export-landing-mode-title'>
                  Full export review
                  <span className='export-landing-badge export-landing-badge-time'>
                    <AccessTimeIcon aria-hidden='true' />
                    5+ min
                  </span>
                </span>
                <span className='export-landing-mode-desc'>
                  {isSpecific
                    ? 'Every check, start to finish.'
                    : 'Every check, start to finish: product profile, Schedule B / HTS, country and control lists, nuclear, ITAR and EAR / CCL.'}
                </span>
              </span>
            </label>
            <span className='export-landing-or' aria-hidden='true'>
              or
            </span>
            <label className={`export-landing-mode${isSpecific ? ' is-selected' : ''}`}>
              <input
                type='radio'
                name='export-landing-scope'
                className='export-landing-visually-hidden'
                checked={isSpecific}
                onChange={() => selectMode('specific')}
                onClick={markTouched}
              />
              <span className='export-landing-radio' aria-hidden='true' />
              <span className='export-landing-mode-body'>
                <span className='export-landing-mode-title'>
                  Only specific checks
                  <span className='export-landing-badge'>Faster</span>
                </span>
                <span className='export-landing-mode-desc'>
                  {isSpecific
                    ? 'Pick one or more below.'
                    : 'Pick one or more, like Schedule B / HTS or the country and control list check.'}
                </span>
              </span>
            </label>
          </div>
        </fieldset>

        {isSpecific && (
          <div
            className='export-landing-picker'
            role='group'
            aria-labelledby='export-landing-picker-label'
          >
            <div className='export-landing-picker-head'>
              <span id='export-landing-picker-label' className='export-landing-label'>
                Pick your checks
              </span>
              {suggested.length > 0 && (
                <span className='export-landing-picker-hint'>
                  We pre-selected these from your description
                </span>
              )}
            </div>
            <div className='export-landing-tiles'>
              {EXPORT_SCOPE_PHASE_ORDER.map((phase) => {
                const on = choice.selectedPhases.includes(phase);
                return (
                  <label
                    key={phase}
                    className={`export-landing-tile${on ? ' is-checked' : ''}`}
                  >
                    <input
                      type='checkbox'
                      className='export-landing-visually-hidden'
                      checked={on}
                      disabled={disabled}
                      onChange={() => togglePhase(phase)}
                    />
                    <span className='export-landing-checkbox' aria-hidden='true'>
                      {on && <CheckIcon />}
                    </span>
                    <span className='export-landing-tile-label'>
                      {EXPORT_SCOPE_CHIP_LABEL[phase]}
                    </span>
                    {suggested.includes(phase) && (
                      <span className='export-landing-suggested'>Suggested</span>
                    )}
                  </label>
                );
              })}
            </div>
            {prereqLines.map((line) => (
              <div key={line} className='export-landing-prereq'>
                {line}
              </div>
            ))}
          </div>
        )}

        <div className='export-landing-footer'>
          <button
            type='submit'
            className='standard-workflow-primary-btn export-landing-start'
            disabled={!canStart}
          >
            {startButtonLabel(choice)}
            <ArrowForwardIcon aria-hidden='true' />
          </button>
        </div>
      </form>
    </div>
  );
};

export default ExportLanding;
